import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BaseAuction } from "../../../types/auction";
import { API_BASE_URL } from "../../../services/apiConfig";
import easyAuctionLogo from "../../../assets/easyauction.png";
import { auctionTypeLabel } from "../../../utils/auctionTypeLabels";

type RGB = [number, number, number];

const C = {
  navy: [15, 23, 42] as RGB,
  blue: [29, 78, 216] as RGB,
  orange: [249, 115, 22] as RGB,
  text: [31, 41, 55] as RGB,
  muted: [107, 114, 128] as RGB,
  line: [226, 232, 240] as RGB,
  soft: [244, 247, 252] as RGB,
  green: [4, 120, 87] as RGB,
  greenSoft: [220, 252, 231] as RGB,
};

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 14;
const CONTENT_W = PAGE_W - MARGIN * 2;
const HEADER_H = 30; // space reserved at top of every page
const FOOTER_H = 14; // space reserved at bottom of every page

/** Loads the brand logo as a PNG data URL (and its aspect ratio) for jsPDF. */
const loadLogo = (): Promise<{ data: string; ratio: number } | null> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(null);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL("image/png"), ratio: img.naturalWidth / img.naturalHeight });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = easyAuctionLogo;
  });

const fmtDate = (v: any) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (v: any) => {
  if (!v) return "-";
  const d = new Date(v);
  return isNaN(d.getTime())
    ? "-"
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Generate and download a detailed PDF report for an auction
 */
export const generateAuctionReport = async (
  auction: Pick<BaseAuction, "id" | "backendId">
): Promise<void> => {
  try {
    /* 1. Fetch complete auction object */
    const token = localStorage.getItem("token") || localStorage.getItem("authToken");
    const res = await fetch(`${API_BASE_URL}/auction/${auction.backendId || auction.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Could not fetch auction detail");

    const json = await res.json();
    const full = json.auction;
    if (!full) throw new Error("Empty auction detail");

    /* 2. Normalise data */
    // The default PDF fonts have no rupee glyph, so print the ISO code instead.
    const cur = !full.currency || full.currency === "₹" ? "INR" : String(full.currency);
    const money = (v: any) => `${cur} ${Number(v || 0).toLocaleString("en-IN")}`;
    const isForward = full.auction_mode === "forward";
    const status = String(full.status || "").toUpperCase();

    const participants = (full.participants || []).map((p: any) => ({
      companyName: p.company_name || "-",
      personName: p.person_name || "-",
      phone: p.email || p.invite_email || p.user_email || p.phone_number || p.phone || "-",
      status: p.status || "invited",
      invitedAt: p.invited_at,
    }));
    const bidHistory: any[] = full.bid_history || full.bids || [];
    const winner = full.winner_info;
    const documents: any[] = full.documents || [];

    /* 3. Build the PDF */
    const doc = new jsPDF("p", "mm", "a4");
    const logo = await loadLogo();
    let y = HEADER_H + 4;

    const setFill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);
    const setText = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
    const setDraw = (c: RGB) => doc.setDrawColor(c[0], c[1], c[2]);
    const ensure = (h: number) => {
      if (y + h > PAGE_H - FOOTER_H) {
        doc.addPage();
        y = HEADER_H + 4;
      }
    };
    const lastY = () => (doc as any).lastAutoTable.finalY as number;

    const sectionTitle = (title: string, note?: string) => {
      ensure(60);
      setFill(C.orange);
      doc.rect(MARGIN, y, 1.2, 5.5, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      setText(C.navy);
      doc.text(title, MARGIN + 4, y + 4.4);
      if (note) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5);
        setText(C.muted);
        doc.text(note, PAGE_W - MARGIN, y + 4.4, { align: "right" });
      }
      y += 9;
    };

    /** Two-column label/value grid rendered as a clean table. */
    const detailGrid = (rows: [string, string][]) => {
      const body: any[] = [];
      for (let i = 0; i < rows.length; i += 2) {
        const a = rows[i];
        const b = rows[i + 1];
        body.push([a[0], a[1], b ? b[0] : "", b ? b[1] : ""]);
      }
      autoTable(doc, {
        startY: y,
        body,
        theme: "plain",
        margin: { left: MARGIN, right: MARGIN, top: HEADER_H + 4, bottom: FOOTER_H },
        styles: { font: "helvetica", fontSize: 9, cellPadding: { top: 2.2, bottom: 2.2, left: 3, right: 3 }, textColor: C.text, lineColor: C.line, lineWidth: { bottom: 0.2 } },
        columnStyles: {
          0: { cellWidth: 32, textColor: C.muted, fontSize: 8 },
          1: { cellWidth: CONTENT_W / 2 - 32, fontStyle: "bold" },
          2: { cellWidth: 32, textColor: C.muted, fontSize: 8 },
          3: { cellWidth: CONTENT_W / 2 - 32, fontStyle: "bold" },
        },
      });
      y = lastY() + 7;
    };

    const dataTable = (head: string[], body: any[][], opts: any = {}) => {
      autoTable(doc, {
        startY: y,
        head: [head],
        body,
        theme: "grid",
        margin: { left: MARGIN, right: MARGIN, top: HEADER_H + 4, bottom: FOOTER_H },
        headStyles: { fillColor: C.navy, textColor: [255, 255, 255], fontSize: 8.5, fontStyle: "bold", halign: "left", cellPadding: 2.6 },
        bodyStyles: { fontSize: 8.5, textColor: C.text, cellPadding: 2.4 },
        alternateRowStyles: { fillColor: C.soft },
        styles: { lineColor: C.line, lineWidth: 0.2, overflow: "linebreak" },
        ...opts,
      });
      y = lastY() + 7;
    };

    /* ---- Summary banner: title + status ---- */
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    setText(C.navy);
    const titleLines = doc.splitTextToSize(String(full.title || "Auction"), CONTENT_W - 34);
    doc.text(titleLines, MARGIN, y + 5);
    // status pill
    const pillW = 28;
    const pillColor: RGB = full.status === "completed" ? C.green : full.status === "live" ? C.orange : C.blue;
    setFill(pillColor);
    doc.roundedRect(PAGE_W - MARGIN - pillW, y, pillW, 7, 3.5, 3.5, "F");
    doc.setFontSize(8);
    setText([255, 255, 255]);
    doc.text(status, PAGE_W - MARGIN - pillW / 2, y + 4.7, { align: "center" });
    y += titleLines.length * 7 + 1;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    setText(C.muted);
    doc.text(`${full.auction_no}  |  ${fmtDate(full.auction_date)}  |  ${full.formatted_start_time || ""} - ${full.formatted_end_time || ""}`, MARGIN, y + 3);
    y += 9;

    /* ---- KPI cards ---- */
    const kpis: [string, string][] = [
      ["TOTAL BIDS", String(full.statistics?.total_bids || 0)],
      ["PARTICIPANTS", String(full.statistics?.total_participants || 0)],
      ["HIGHEST BID", money(full.statistics?.highest_bid)],
      ["LOWEST BID", money(full.statistics?.lowest_bid)],
    ];
    const gap = 4;
    const cardW = (CONTENT_W - gap * 3) / 4;
    kpis.forEach(([label, value], i) => {
      const x = MARGIN + i * (cardW + gap);
      setFill(C.soft);
      setDraw(C.line);
      doc.roundedRect(x, y, cardW, 18, 2, 2, "FD");
      setFill(C.blue);
      doc.rect(x, y + 2, 1, 14, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      setText(C.muted);
      doc.text(label, x + 4, y + 6);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(value.length > 14 ? 10 : 12);
      setText(C.navy);
      doc.text(value, x + 4, y + 13.5);
    });
    y += 25;

    /* ---- Winner card ---- */
    if (winner && winner.amount != null) {
      ensure(34);
      setFill(C.greenSoft);
      setDraw(C.green);
      doc.setLineWidth(0.4);
      doc.roundedRect(MARGIN, y, CONTENT_W, 28, 2.5, 2.5, "FD");
      doc.setLineWidth(0.2);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      setText(C.green);
      doc.text(isForward ? "WINNING BID (HIGHEST)" : "WINNING BID (LOWEST)", MARGIN + 6, y + 7);
      doc.setFontSize(20);
      doc.text(money(winner.amount), MARGIN + 6, y + 19);
      const bidder = full.bids?.find?.((b: any) => Number(b.user_id) === Number(winner.user_id));
      const info: [string, string][] = [
        ["Company", winner.company_name || "-"],
        ["Bidder", winner.person_name || "-"],
        ["Bid time", fmtDateTime(bidder?.bid_time || winner.bid_time)],
      ];
      let iy = y + 7;
      info.forEach(([k, v]) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        setText(C.muted);
        doc.text(k, MARGIN + 78, iy);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        setText(C.navy);
        doc.text(String(v), MARGIN + 100, iy);
        iy += 7;
      });
      y += 35;
    } else if (full.status === "completed") {
      ensure(20);
      setFill([254, 243, 199]);
      doc.roundedRect(MARGIN, y, CONTENT_W, 14, 2, 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      setText([146, 64, 14]);
      doc.text("Auction completed with no winning bid.", MARGIN + 6, y + 8.5);
      y += 21;
    }

    /* ---- Auction details ---- */
    sectionTitle("Auction Details");
    detailGrid([
      ["Auction No", full.auction_no || "-"],
      ["Status", status || "-"],
      ["Date", fmtDate(full.auction_date)],
      ["Time", `${full.formatted_start_time || "-"} - ${full.formatted_end_time || "-"}`],
      ["Duration", full.duration ? `${Math.round(Number(full.duration) / 60)} minutes` : "-"],
      ["Pre-bid", full.pre_bid_allowed ? "Allowed" : "Not allowed"],
      ["Currency", cur],
      [isForward ? "Increment" : "Decrement", money(full.decremental_value)],
      ["Auction Type", auctionTypeLabel(full.auction_type)],
      ["Bid Direction", isForward ? "Forward (highest wins)" : "Reverse (lowest wins)"],
    ]);

    if (full.description) {
      ensure(24);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(8);
      setText(C.muted);
      doc.text("DESCRIPTION", MARGIN, y);
      y += 4;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      setText(C.text);
      const lines = doc.splitTextToSize(String(full.description), CONTENT_W);
      lines.forEach((ln: string) => {
        ensure(5);
        doc.text(ln, MARGIN, y);
        y += 4.6;
      });
      y += 5;
    }

    /* ---- Auctioneer ---- */
    sectionTitle("Auctioneer");
    detailGrid([
      ["Company", full.creator_info?.company_name || "-"],
      ["Contact Person", full.creator_info?.person_name || "-"],
      ["Email", full.creator_info?.email || full.auctioneer_email || "-"],
      ["Phone", full.creator_info?.phone || full.creator_info?.phone_number || "-"],
    ]);

    /* ---- Bid history ---- */
    sectionTitle("Complete Bid History", `${bidHistory.length} bid${bidHistory.length === 1 ? "" : "s"}`);
    if (bidHistory.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9.5);
      setText(C.muted);
      doc.text("No bids were placed in this auction.", MARGIN, y + 4);
      y += 12;
    } else {
      const isWinnerBid = (b: any) =>
        winner &&
        Number(b.user_id) === Number(winner.user_id) &&
        Number(b.amount) === Number(winner.amount);
      let winnerMarked = false;
      const rows = bidHistory.map((b, i) => {
        const isWin = !winnerMarked && isWinnerBid(b);
        if (isWin) winnerMarked = true;
        return [
          String(i + 1),
          b.company_name || "-",
          b.person_name || b.bidder_name || "-",
          money(b.amount),
          fmtDateTime(b.bid_time),
          isWin ? "WINNER" : "",
        ];
      });
      dataTable(["#", "Company", "Bidder", "Bid Amount", "Bid Time", "Result"], rows, {
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 44 },
          2: { cellWidth: 34 },
          3: { cellWidth: 32, halign: "right", fontStyle: "bold" },
          4: { cellWidth: 38 },
          5: { cellWidth: 24, halign: "center", fontStyle: "bold", textColor: C.green },
        },
        didParseCell: (d: any) => {
          if (d.section === "body" && d.row.raw[5] === "WINNER") {
            d.cell.styles.fillColor = C.greenSoft;
            d.cell.styles.textColor = C.green;
            d.cell.styles.fontStyle = "bold";
          }
        },
      });
    }

    /* ---- Participants ---- */
    const joined = participants.filter((p: any) => p.status === "joined").length;
    sectionTitle("Participants", `${participants.length} total  |  ${joined} joined`);
    if (participants.length === 0) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9.5);
      setText(C.muted);
      doc.text("No participants were invited to this auction.", MARGIN, y + 4);
      y += 12;
    } else {
      dataTable(
        ["#", "Company", "Contact Person", "Email / Phone", "Status", "Invited"],
        participants.map((p: any, i: number) => [
          String(i + 1),
          p.companyName,
          p.personName,
          p.phone,
          String(p.status).toUpperCase(),
          fmtDate(p.invitedAt),
        ]),
        {
          columnStyles: {
            0: { cellWidth: 10, halign: "center" },
            1: { cellWidth: 46 },
            2: { cellWidth: 36 },
            3: { cellWidth: 30 },
            4: { cellWidth: 24, halign: "center" },
            5: { cellWidth: 36 },
          },
        }
      );
    }

    /* ---- Documents ---- */
    if (documents.length > 0) {
      sectionTitle("Attached Documents", `${documents.length} file${documents.length === 1 ? "" : "s"}`);
      dataTable(
        ["#", "File Name", "Type", "Uploaded"],
        documents.map((d: any, i: number) => [String(i + 1), d.file_name, String(d.file_type || "").toUpperCase(), fmtDate(d.uploaded_at)]),
        { columnStyles: { 0: { cellWidth: 10, halign: "center" }, 1: { cellWidth: 100 }, 2: { cellWidth: 30, halign: "center" }, 3: { cellWidth: 42 } } }
      );
    }

    /* 4. Header + footer on every page (drawn last so page count is known) */
    const total = doc.getNumberOfPages();
    const generated = new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    for (let p = 1; p <= total; p++) {
      doc.setPage(p);

      // header
      if (logo) {
        const h = 15;
        doc.addImage(logo.data, "PNG", MARGIN - 3, 6, h * logo.ratio, h, undefined, "FAST");
      } else {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        setText(C.navy);
        doc.text("EasyEAuction", MARGIN, 15);
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      setText(C.navy);
      doc.text("AUCTION REPORT", PAGE_W - MARGIN, 12, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      setText(C.muted);
      doc.text(String(full.auction_no || ""), PAGE_W - MARGIN, 17, { align: "right" });
      doc.text(`Generated ${generated}`, PAGE_W - MARGIN, 21.5, { align: "right" });
      setFill(C.blue);
      doc.rect(MARGIN, 25, CONTENT_W - 30, 0.8, "F");
      setFill(C.orange);
      doc.rect(MARGIN + CONTENT_W - 30, 25, 30, 0.8, "F");

      // footer
      setDraw(C.line);
      doc.line(MARGIN, PAGE_H - 11, PAGE_W - MARGIN, PAGE_H - 11);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      setText(C.muted);
      doc.text("Computer-generated report from EasyEAuction. Confidential - for authorised use only.", MARGIN, PAGE_H - 6.5);
      doc.text(`Page ${p} of ${total}`, PAGE_W - MARGIN, PAGE_H - 6.5, { align: "right" });
    }

    /* 5. Download */
    doc.save(`Auction_Report_${full.auction_no}.pdf`);
  } catch (error) {
    console.error("Failed to generate PDF", error);
    throw error;
  }
};
